import _ from 'lodash';

import {
  AssessmentQuestion,
  AssessmentQuestionType,
  MultipleChoiceAssessmentQuestion,
  MultipleChoiceScoredAssessmentQuestion,
  MultiSelectAssessmentQuestion,
  FreeTextAssessmentQuestion,
  SignatureAssessmentQuestion,
  MultiSignatureAssessmentQuestion,
  RichTextAssessmentQuestion,
  InitialsAssessmentQuestion,
  AutofillAssessmentQuestion,
  DatePickerAssessmentQuestion,
  FileUploadAssessmentQuestion,
  ProviderSignatureAssessmentQuestion,
  PatientContactAssessmentQuestion,
  CompleteAssessmentAnswerInput,
  MultipleChoiceScoredAssessmentQuestionAnswerOption,
  PatientCreatePatientContactInput,
  ContactType,
} from '../../generated/graphql';

import {
  isSignatureTypeAssessmentQuestion,
  isMultiSelectAssessmentQuestion,
  isFileUploadAssessmentQuestion,
  isFreeTextAssessmentQuestion,
  isRichTextAssessmentQuestion,
  isMultipleChoiceAssessmentQuestion,
  isMultipleChoiceScoredAssessmentQuestion,
  isPatientContactAssessmentQuestion,
  isConditionalMultipleChoiceAssessmentQuestion,
} from './assessments';

// Shape the form uses: ProgramAssessmentQuestion (discriminated union with
// concrete fields like answerOptions, assessmentSignatureUsers) plus an
// optional conditionalFollowUp pointer for ConditionalMultipleChoice entries.
export type FormQuestion = ProgramAssessmentQuestion & {
  conditionalFollowUp?: {
    followUpIndex: number;
    followUpWhen: string[];
  };
};

export const flattenQuestionsForForm = (
  questions: (AssessmentQuestion | null | undefined)[],
): FormQuestion[] => {
  const flat: FormQuestion[] = [];
  questions.forEach((q, inputIndex) => {
    if (!q) {
      console.warn(
        '[flattenQuestionsForForm] Skipping null/undefined question entry at index',
        inputIndex,
      );
      return;
    }
    if (isConditionalMultipleChoiceAssessmentQuestion(q) && q.followUp) {
      const followUpIndex = flat.length + 1;
      flat.push({
        __typename: 'MultipleChoiceAssessmentQuestion',
        questionType: AssessmentQuestionType.MultipleChoice,
        isRequired: q.isRequired,
        question: q.question,
        answerOptions: q.answerOptions,
        conditionalFollowUp: {
          followUpIndex,
          followUpWhen: q.followUpWhen,
        },
      } as FormQuestion);
      flat.push({
        __typename: 'MultipleChoiceAssessmentQuestion',
        questionType: AssessmentQuestionType.MultipleChoice,
        isRequired: false,
        question: q.followUp.question,
        answerOptions: q.followUp.answerOptions,
      } as FormQuestion);
    } else {
      flat.push(q as FormQuestion);
    }
  });
  return flat;
};

type AssessmentQuestionWithAnswers =
  | MultipleChoiceAssessmentQuestion
  | MultipleChoiceScoredAssessmentQuestion
  | MultiSelectAssessmentQuestion;

export type ProgramAssessmentQuestion =
  | FreeTextAssessmentQuestion
  | InitialsAssessmentQuestion
  | SignatureAssessmentQuestion
  | MultiSignatureAssessmentQuestion
  | ProviderSignatureAssessmentQuestion
  | RichTextAssessmentQuestion
  | AutofillAssessmentQuestion
  | DatePickerAssessmentQuestion
  | FileUploadAssessmentQuestion
  | PatientContactAssessmentQuestion
  | AssessmentQuestionWithAnswers;

export const PATIENT_CONTACT_NEW_SENTINEL = '__HC_NEW_CONTACT__';

export type PatientContactNewDraft = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  relationshipDescription: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressPostalCode: string;
  addressCountry: string;
};

export function emptyPatientContactNewDraft(): PatientContactNewDraft {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    relationshipDescription: '',
    addressStreet: '',
    addressCity: '',
    addressState: '',
    addressPostalCode: '',
    addressCountry: '',
  };
}

export function isPatientContactNewDraftComplete(
  d: PatientContactNewDraft | null | undefined,
): boolean {
  if (!d) {
    return false;
  }
  return Boolean(
    d.firstName?.trim() &&
      d.lastName?.trim() &&
      d.email?.trim() &&
      d.phoneNumber?.trim(),
  );
}

/** True if any draft field has non-whitespace input (used for optional questions). */
export function hasPatientContactNewDraftAnyInput(
  d: PatientContactNewDraft | null | undefined,
): boolean {
  if (!d) {
    return false;
  }
  return Object.values(d).some((v) => String(v ?? '').trim() !== '');
}

export function patientContactDraftToCreateInput(
  contactType: ContactType,
  d: PatientContactNewDraft,
): PatientCreatePatientContactInput {
  return {
    contactType,
    firstName: d.firstName.trim(),
    lastName: d.lastName.trim(),
    email: d.email.trim(),
    phoneNumber: d.phoneNumber.trim(),
    relationshipDescription: d.relationshipDescription?.trim() || undefined,
    addressStreet: d.addressStreet?.trim() || undefined,
    addressCity: d.addressCity?.trim() || undefined,
    addressState: d.addressState?.trim() || undefined,
    addressPostalCode: d.addressPostalCode?.trim() || undefined,
    addressCountry: d.addressCountry?.trim() || undefined,
  };
}

export type AssessmentAnswerInput = {
  value: string;
  values: string[];
  signatureName?: string;
  signatureTermsAgreed?: boolean;
  mediaId?: string;
  fileName?: string;
  clientFileId?: string;
  contactId?: string;
  newContactDraft?: PatientContactNewDraft;
};

export function mapAssessmentAnswersToMutationInput(
  questions: FormQuestion[],
  answers: AssessmentAnswerInput[],
): CompleteAssessmentAnswerInput[] {
  return questions.map((question, idx) => {
    // Safety net: the caller must pass the flattened question list.
    // ConditionalMultipleChoice entries are a form-only concept and must never
    // appear here; submitting them would fail server-side schema validation.
    if (
      process.env.NODE_ENV !== 'production' &&
      question.questionType === AssessmentQuestionType.ConditionalMultipleChoice
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[mapAssessmentAnswersToMutationInput] Received a ConditionalMultipleChoice question at index',
        idx,
        '— pass flattenedQuestions (not raw questions) to this function.',
      );
    }
    const answerValue = answers?.[idx]?.value ?? '';
    const newAnswer: CompleteAssessmentAnswerInput = {
      question: question.question,
      questionType: question.questionType,
    };

    if (isFileUploadAssessmentQuestion(question)) {
      // For file upload answers, we store the mediaId in the answer field
      // The backend will use this to reference the already-uploaded file
      // Always send answer field (mediaId or empty string) to explicitly clear/overwrite previous files
      newAnswer.answer = answers?.[idx]?.mediaId ?? '';
    } else if (isSignatureTypeAssessmentQuestion(question)) {
      if (answers?.[idx]?.signatureName) {
        newAnswer.signatureName = answers[idx].signatureName;
      }
    } else if (
      isFreeTextAssessmentQuestion(question) ||
      isRichTextAssessmentQuestion(question) ||
      isMultipleChoiceAssessmentQuestion(question) ||
      isMultipleChoiceScoredAssessmentQuestion(question)
    ) {
      // Always include answer field for FreeText, RichText, MultipleChoice, and MultipleChoiceScored questions (required by schema)
      // Empty string is valid when question is left blank
      newAnswer.answer = answerValue || '';
    } else if (answerValue && answerValue.trim() !== '') {
      // For other question types, only set answer if it has a value (don't send empty strings)
      newAnswer.answer = answerValue;
    }

    if (isMultiSelectAssessmentQuestion(question)) {
      // Always include answers field for MultiSelect questions (required by schema)
      // Empty array is valid when no options are selected
      const multiSelectAnswers: string[] = answers?.[idx]?.values ?? [];
      newAnswer.answers = multiSelectAnswers;
    }

    if (isPatientContactAssessmentQuestion(question)) {
      const contactId = answers?.[idx]?.contactId?.trim();
      if (contactId && contactId !== PATIENT_CONTACT_NEW_SENTINEL) {
        newAnswer.contactId = contactId;
      }
      const draft = answers?.[idx]?.newContactDraft;
      if (draft && Object.values(draft).some((v) => String(v ?? '').trim())) {
        newAnswer.newContactDraftJson = JSON.stringify(draft);
      }
    }

    const answerOptions =
      (question as MultipleChoiceScoredAssessmentQuestion)?.answerOptions ?? [];

    const answer = answerOptions.find(
      (option) => option.answerOption === answerValue,
    );

    const multipleChoiceSumAnswer =
      answer as MultipleChoiceScoredAssessmentQuestionAnswerOption;
    if (multipleChoiceSumAnswer && !_.isNil(multipleChoiceSumAnswer.score)) {
      newAnswer.score = multipleChoiceSumAnswer.score;
    }

    const multipleChoiceQuestion =
      question as MultipleChoiceScoredAssessmentQuestion;
    if (multipleChoiceQuestion && !_.isNil(multipleChoiceQuestion.subscale)) {
      newAnswer.subscale = multipleChoiceQuestion.subscale;
    }

    return newAnswer;
  });
}

/**
 * Creates PatientContact rows for answers still using PATIENT_CONTACT_NEW_SENTINEL
 * before completing the program activity.
 */
export async function resolvePatientContactAnswersForSubmit(
  questions: FormQuestion[],
  answers: AssessmentAnswerInput[],
  createPatientContact: (
    input: PatientCreatePatientContactInput,
  ) => Promise<{ id?: string | null } | null | undefined>,
): Promise<AssessmentAnswerInput[]> {
  const next: AssessmentAnswerInput[] = answers.map((a) => ({
    ...a,
    newContactDraft: a.newContactDraft ? { ...a.newContactDraft } : undefined,
  }));

  const createdIdByDraftKey = new Map<string, string>();

  for (let i = 0; i < questions.length; i++) {
    const q = questions[i];
    if (!isPatientContactAssessmentQuestion(q)) {
      continue;
    }

    const raw = (next[i].contactId ?? '').trim();
    const draft = next[i].newContactDraft;
    const isExplicitNew = raw === PATIENT_CONTACT_NEW_SENTINEL;
    const isAlreadyResolved =
      raw !== '' && raw !== PATIENT_CONTACT_NEW_SENTINEL;
    const isAddWithoutPicker =
      raw === '' && isPatientContactNewDraftComplete(draft);

    if (isAlreadyResolved) {
      next[i] = { ...next[i], newContactDraft: undefined };
      continue;
    }

    if (!isExplicitNew && !isAddWithoutPicker) {
      if (q.isRequired) {
        throw new Error(
          'Please complete all required contact fields for each Client Contact question.',
        );
      }
      continue;
    }

    if (!isPatientContactNewDraftComplete(draft)) {
      if (q.isRequired) {
        throw new Error(
          'Please complete all required contact fields for each Client Contact question.',
        );
      }
      if (hasPatientContactNewDraftAnyInput(draft)) {
        throw new Error(
          'Complete all required contact fields for this contact question, or clear the form to skip it.',
        );
      }
      next[i] = {
        ...next[i],
        contactId: undefined,
        newContactDraft: undefined,
      };
      continue;
    }

    const input = patientContactDraftToCreateInput(q.contactType, draft!);
    const draftKey = `${q.contactType}:${JSON.stringify(input)}`;
    const existingId = createdIdByDraftKey.get(draftKey);
    if (existingId) {
      next[i] = {
        ...next[i],
        contactId: existingId,
        newContactDraft: undefined,
      };
      continue;
    }

    const created = await createPatientContact(input);
    const id = created?.id?.trim();
    if (!id) {
      throw new Error('Could not save a new contact. Please try again.');
    }
    createdIdByDraftKey.set(draftKey, id);
    next[i] = {
      ...next[i],
      contactId: id,
      newContactDraft: undefined,
    };
  }

  return next;
}
